import { subDays } from "date-fns";
import { useActivityGroup } from "web_myplann/Activities/Hooks/useActivityGroup";
import { useActivityListInfiniteQuery } from "web_myplann/Api/Activities/useActivityListInfiniteQuery";
import { useEventsInfiniteQuery } from "web_myplann/Api/Activities/useEventsInfiniteQuery";
import { useTaskListInfiniteQuery } from "web_myplann/Api/Tasks/useTaskListInfiniteQuery";
import {
	dateFormatter,
	ISO_DATE_FORMAT,
} from "web_myplann/Shared/Utils/dateFormatters";

type UseActivitiesListOptions = {
	upcomingUntil?: string;
	overdueFrom?: string;
	includeResolvedEvents?: boolean;
	pageSize?: number;
};

export const useActivitiesList = ({
	upcomingUntil,
	overdueFrom,
	includeResolvedEvents = true,
	pageSize,
}: UseActivitiesListOptions = {}) => {
	const currentDate = new Date();
	const actualDate = dateFormatter(currentDate, ISO_DATE_FORMAT);
	const yesterdayDate = dateFormatter(subDays(currentDate, 1), ISO_DATE_FORMAT);

	const inProgressQuery = useTaskListInfiniteQuery(
		undefined,
		undefined,
		true,
		undefined,
		false,
		pageSize,
	);
	const upcomingQuery = useActivityListInfiniteQuery(
		actualDate,
		upcomingUntil,
		true,
		undefined,
		false,
		pageSize,
	);
	const overdueQuery = useActivityListInfiniteQuery(
		overdueFrom,
		yesterdayDate,
		true,
		undefined,
		true,
		pageSize,
	);
	const resolvedQuery = useEventsInfiniteQuery(
		undefined,
		undefined,
		undefined,
		true,
		true,
		pageSize,
		{ enabled: includeResolvedEvents },
	);

	const solvedEvents = useActivityGroup(inProgressQuery);
	const upcomingEvents = useActivityGroup(upcomingQuery);
	const overdueEvents = useActivityGroup(overdueQuery);
	const resolvedEvents = useActivityGroup(resolvedQuery);

	const isLoading =
		inProgressQuery.isLoading ||
		upcomingQuery.isLoading ||
		overdueQuery.isLoading ||
		resolvedQuery.isLoading;

	const allSectionsEmpty =
		!solvedEvents.totalCount &&
		!upcomingEvents.totalCount &&
		!overdueEvents.totalCount &&
		(!includeResolvedEvents || !resolvedEvents.totalCount) &&
		!isLoading;

	const hasOtherSections =
		upcomingEvents.totalCount > 0 ||
		overdueEvents.totalCount > 0 ||
		(includeResolvedEvents && resolvedEvents.totalCount > 0);

	return {
		solvedEvents,
		upcomingEvents,
		overdueEvents,
		resolvedEvents,
		isLoading,
		allSectionsEmpty,
		hasOtherSections,
	};
};
